import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert
} from '@mui/material';
import userService from '../../services/userService';
import companyService from '../../services/companyService';
import { useSelector } from 'react-redux';

const UserFormModal = ({ open, onClose, mode = 'create', initialUser = null, onSuccess }) => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const isSystemAdmin = !currentUser?.companyId;

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    userType: 'company_member',
    companyId: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [companies, setCompanies] = useState([]);
  const isEdit = mode === 'edit' && initialUser;

  useEffect(() => {
    if (open) {
      // Fetch companies for admins/special users (no companyId)
      if (isSystemAdmin) {
        companyService.getCompanies().then(setCompanies).catch(() => setCompanies([]));
      }
      if (isEdit) {
        setForm({
          firstName: initialUser.firstName || '',
          lastName: initialUser.lastName || '',
          email: initialUser.email || '',
          userType: initialUser.userType || 'company_member',
          companyId: initialUser.companyId?._id || '',
          password: ''
        });
      } else {
        setForm({
          firstName: '',
          lastName: '',
          email: '',
          userType: 'company_member',
          companyId: '',
          password: ''
        });
      }
      setError('');
    }
  }, [open, isEdit, initialUser, isSystemAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      // If userType changes to system_admin or individual, clear companyId
      if (name === 'userType') {
        const next = { ...prev, [name]: value };
        if (value === 'system_admin' || value === 'individual') {
          next.companyId = '';
        }
        return next;
      }
      return { ...prev, [name]: value };
    });
  };

  // Determine allowed userType options based on requester
  const getAllowedUserTypes = () => {
    if (currentUser?.userType === 'system_admin') {
      return ['system_admin', 'company_owner', 'company_member', 'individual'];
    }
    if (currentUser?.companyId) {
      // Company user
      if (currentUser?.userType === 'company_owner') {
        return ['company_owner', 'company_member'];
      }
      return ['company_member'];
    }
    // Special user with no company (but has permission): cannot create system_admin
    return ['company_owner', 'company_member', 'individual'];
  };

  const allowedUserTypes = getAllowedUserTypes();

  const handleSubmit = async () => {
    try {
      setError('');
      if (!form.firstName || !form.lastName || !form.email) {
        setError('Ad, soyad ve email zorunludur');
        return;
      }
      // If no requester companyId and selected userType requires company, enforce selection
      const requiresCompany = !currentUser?.companyId && (form.userType === 'company_owner' || form.userType === 'company_member');
      if (!isEdit && requiresCompany && !form.companyId) {
        setError('Şirket sahibi/üyesi için şirket seçimi zorunludur');
        return;
      }
      if (isEdit) {
        const payload = {
          firstName: form.firstName,
          lastName: form.lastName
        };
        const updated = await userService.updateUser(initialUser._id, payload);
        onSuccess?.(updated.user);
      } else {
        const payload = {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          userType: allowedUserTypes.includes(form.userType) ? form.userType : allowedUserTypes[0],
          password: form.password || undefined
        };
        if (!currentUser?.companyId && form.companyId) {
          payload.companyId = form.companyId;
        }
        const created = await userService.createUser(payload);
        onSuccess?.(created.user);
      }
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'İşlem başarısız oldu');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Kullanıcıyı Düzenle' : 'Yeni Kullanıcı Oluştur'}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Ad"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Soyad"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              required
              disabled={isEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Kullanıcı Tipi</InputLabel>
              <Select
                label="Kullanıcı Tipi"
                name="userType"
                value={form.userType}
                onChange={handleChange}
                disabled={isEdit}
              >
                {allowedUserTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t === 'company_member' && 'Şirket Üyesi'}
                    {t === 'company_owner' && 'Şirket Sahibi'}
                    {t === 'individual' && 'Bireysel'}
                    {t === 'system_admin' && 'Sistem Yöneticisi'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* Company select: shown only when requester has no company AND creating AND type is company_owner/member */}
          {!currentUser?.companyId && !isEdit && form.userType !== 'system_admin' && form.userType !== 'individual' && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Şirket</InputLabel>
                <Select
                  label="Şirket"
                  name="companyId"
                  value={form.companyId}
                  onChange={handleChange}
                  sx={{ minWidth: 240 }}
                >
                  <MenuItem value="" disabled>Şirket Seçin</MenuItem>
                  {companies.map((c) => (
                    <MenuItem key={c._id} value={c._id}>{c.companyName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {!isEdit && (
            <Grid size={12}>
              <TextField
                label="Geçici Şifre (opsiyonel)"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                fullWidth
                placeholder="En az 8 karakter bırakılırsa otomatik atanır"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>İptal</Button>
        <Button variant="contained" onClick={handleSubmit}>{isEdit ? 'Kaydet' : 'Oluştur'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormModal;


